<script setup lang="ts">
import type { ValidationRule } from '#layers/access/types/permissions/policy';
import type { InputBlockProps } from '#layers/blocks/public';
import type { GlobalCollectionId } from '#layers/data-model/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { ButtonAdd, ListCard } from '#layers/ui/public';

interface Props extends InputBlockProps {
	collectionId: GlobalCollectionId | null;
}

defineProps<Props>();

const { t } = useI18n();
const appConfig = useAppConfig();

const modelValue = defineModel<ValidationRule[]>({ required: true, default: () => [] });
const items = computed(() => modelValue.value.map((rule, index) => ({ id: index, ...rule })));

function addValidation() {
	modelValue.value = [...modelValue.value, { filter: null }];
}

function removeValidation(index: number) {
	modelValue.value = modelValue.value.toSpliced(index, 1);
}

function clearValidation() {
	modelValue.value = [];
}
</script>

<template>
	<ListCard
		:disabled
		:items
		:ui="{
			item: {
				base: 'px-2',
				content: '',
			},

		}"
	>
		<template #header>
			<span class="text-xs font-medium text-toned">{{ t('common.n_items', modelValue.length) }}</span>
		</template>
		<template #header-actions>
			<div class="-me-2 flex items-center gap-2">
				<DButton
					v-if="modelValue.length > 0"
					size="sm"
					variant="ghost"
					color="neutral"
					:leading-icon="appConfig.ui.icons.close"
					:label="t('policy.validation_rules.clear_validation_rules')"
					:disabled
					@click="clearValidation"
				/>
				<ButtonAdd
					size="sm"
					:label="t('policy.validation_rules.add_validation_rule')"
					:disabled
					@click="addValidation"
				/>
			</div>
		</template>
		<template #item-content="{ index }">
			<div class="flex w-full">
				<InputFilterBlock
					v-if="modelValue[index]"
					v-model="modelValue[index].filter"
					:collection-id
					class="flex-1"
					hide-border
					enable-variables
				/>
				<div class="flex flex-col justify-start gap-1 pt-2">
					<DTooltip :text="t('common.remove')" :delay-duration="0">
						<DButton
							class="opacity-0 group-hover/list-item:opacity-100 group-focus-visible/list-item:opacity-100"
							:icon="appConfig.ui.icons.close"
							color="neutral"
							variant="link"
							size="sm"
							@click="removeValidation(index)"
						/>
					</DTooltip>
				</div>
			</div>
		</template>
	</ListCard>
</template>
